import { globalObservability } from '@opendoor/observability/slim';
import { GraphQLClient } from 'graphql-request';
import type { IncomingHttpHeaders } from 'http';
import Cookies from 'js-cookie';

import type { OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product } from '../../__generated__/athena/extraTypes';
import {
  AddressAutocompleteDocument,
  GetBuyDirectPropertyInfoDocument,
  GetCurrentCustomerDocument,
  GetGeoShapeFromUserLocationDocument,
  GetPropertiesFromGeoShapeDocument,
  GetResaleOffersDocument,
  GetUnlockPropertyInfoDocument,
  UpdateCurrentCustomerDocument,
  UpsertCommPreferenceDocument,
} from '../../__generated__/athena/graphql';
import type {
  GetSeveralFeatureFlagsRequestQueryVariables,
  OdProtosCustomerV1Services_CustomerRequests_UpdateCurrentInput,
  OdProtosCustomerV1Services_UpsertCommPreferenceRequestInput,
  OdProtosGeoDataStoreServices_ShapeGeoOption,
  OdProtosPropertyDataStoreServices_PaginationRequestInput,
  OdProtosPropertyDataStoreServices_PropertyFiltersInput,
  PageInput,
  PropertyFilters,
} from '../../__generated__/athena/graphql';
import { ATHENA_URL, DEPLOY_ENV, RAILS_URL, XSRF_COOKIE_KEY } from '../globals';
import { getLocalFeatureFlagOverrides } from '../helpers/hooks';
import { getAnonymousId } from '../shared/trackers';
import type { IAddressInputAddress } from './addressTypes';

interface fetchWithRetryOptions extends RequestInit {
  retries?: number;
  retryDelay?: number; // in milliseconds
}

/**
 * A wrapper around `fetch` with optional retries.
 * Retries up to `options.retries` times (defaults to `5` if not specified)
 * with a delay of `options.retryDelay` (defaults to `500ms` if not specified)
 */
const fetchWithRetry = async (url: string, options: fetchWithRetryOptions): Promise<Response> => {
  const retries = options.retries || 0;
  const retryDelay = options.retryDelay || 500;

  try {
    return await fetch(url, options);
  } catch (err) {
    if (retries < 1) throw err;

    // sleep the specified amount of time, then recursively retry
    await new Promise((r) => setTimeout(r, retryDelay));
    return await fetchWithRetry(url, { ...options, retries: retries - 1 });
  }
};

const Sentry = globalObservability.getSentryClient();

interface IPostAddressOptions {
  agent_request?: boolean;
  channel?: string;
  covid19Whitelist?: boolean;
  experiment_entity_id?: string;
  manual?: boolean;
  seller_flow_name_override?: string;
  seller_flow_uuid_override?: string;
  trackingKeywords?: string;
  partnerReferralId?: string;
}

// To do: strengthen jsonParam's type
// Do not pass skip doBFFPost as it will not actually skip making the request - instead handle it conditionally
export function doBFFPost(
  apiUrl: string,
  jsonParams: object,
  options: Omit<fetchWithRetryOptions, 'method'> = {},
): Promise<Response> {
  return fetchWithRetry(apiUrl, {
    retries: 5,
    retryDelay: 500,
    credentials: 'include',
    method: 'POST',
    body: JSON.stringify(jsonParams),
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': Cookies.get(XSRF_COOKIE_KEY) || '',
      ...options.headers,
    },
  });
}

export function getAthenaClient(
  incomingRequestHeaders?: IncomingHttpHeaders,
  options?: { signal?: AbortSignal },
) {
  // Use explicit headers if provided, otherwise try AsyncLocalStorage context
  // This allows SSR pages to wrap their logic with withRequestContext() and have
  // all nested getAthenaClient() calls automatically receive headers
  let effectiveHeaders = incomingRequestHeaders;

  // Only import requestContext on server-side to prevent webpack bundling async_hooks for client
  if (!effectiveHeaders && typeof window === 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { getRequestHeaders } = require('./requestContext');
    effectiveHeaders = getRequestHeaders();
  }

  let tivoHeaders: any = [];
  if (DEPLOY_ENV === 'test') {
    if (effectiveHeaders) {
      // Filter out any headers that are not prefixed with 'x-tivo-'
      tivoHeaders = Object.fromEntries(
        Object.entries(effectiveHeaders).filter(([key]) => key.startsWith('x-tivo-')),
      );
    }
  }

  // Forward cookies for SSR authentication
  const headers: Record<string, string> = {
    'apollographql-client-name': 'cosmos',
    ...tivoHeaders,
  };

  // If we have request headers (explicit or from context), forward the cookie header
  if (effectiveHeaders?.cookie) {
    headers.cookie = effectiveHeaders.cookie;
  }

  // Local development auth bypass - send user token to local Athena
  // eslint-disable-next-line @opendoor/no-direct-env-check
  const devUserToken =
    process.env.ATHENA_DEV_USER_TOKEN || process.env.NEXT_PUBLIC_ATHENA_DEV_USER_TOKEN;
  if (DEPLOY_ENV === 'development' && devUserToken) {
    headers['x-athena-request-user'] = devUserToken;
  }

  // Return the graphql-request client directly; call sites invoke
  // `.request(SomeDocument, variables)` with a generated TypedDocumentNode,
  // which infers result/variable types without a generated SDK.
  return new GraphQLClient(ATHENA_URL, {
    credentials: 'include',
    headers,
    // graphql-request vendors its own DOM type defs whose AbortSignal
    // diverges from the native type. Identical at runtime.
    signal: options?.signal ?? undefined,
  });
}

export function doGet(
  path: string,
  options: Omit<fetchWithRetryOptions, 'body' | 'method'> = {},
): Promise<Response> {
  return fetchWithRetry(`${RAILS_URL}${path}`, {
    retries: 5,
    retryDelay: 500,
    credentials: 'include',
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': Cookies.get(XSRF_COOKIE_KEY) || '',
      ...options.headers,
    },
  });
}

function trackLead(trackingKeywords: string) {
  return doGet(`/track/${trackingKeywords}`);
}

export const createSellerInput = async (
  { street1, unit, city, postal_code, state }: IAddressInputAddress,
  {
    product,
    url,
  }: {
    product: OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product;
    url: string;
  },
  {
    channel,
    covid19Whitelist,
    experiment_entity_id: experimentEntityId,
    seller_flow_name_override: sellerFlowNameOverride,
    seller_flow_uuid_override: sellerFlowUuidOverride,
    trackingKeywords,
    partnerReferralId,
  }: IPostAddressOptions = {},
  addressUUID?: string,
) => {
  if (trackingKeywords) {
    await trackLead(trackingKeywords).catch((e) => Sentry.captureException?.(e));
  }

  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'CreateSellerInputWithTrackingDataFromCookies',
      query: `mutation CreateSellerInputWithTrackingDataFromCookies(
        $input: OdProtosReceptionServices_CreateSellerInputRequestInput!
      ) {
        reception {
          createSellerInputWithTrackingDataFromCookies(input: $input) {
            sellerInput {
              uuid
              pdpUrl
              isInPartnershipDenialServiceRegions
              address {
                postalCode
              }
              analyticsMetadata {
                addressEntry {
                  product
                  url
                }
              }
            }
          }
        }
      }`,
      variables: {
        input: {
          address: {
            uuid: addressUUID,
            city,
            postalCode: postal_code,
            state,
            street: street1,
            unit,
          },
          channel,
          partnerReferralId,
          experimentEntityId,
          sellerFlowNameOverride,
          sellerFlowUuidOverride,
          covid19Whitelist,
          analyticsMetadata: {
            addressEntry: {
              product,
              url,
            },
          },
        },
      },
    },
    { headers: { 'apollographql-client-name': 'cosmos' } },
  ).then(handleResponse);
};

export const updateAnswers = async (
  answers: Record<string, unknown>,
  sellerFlowLastCompletedSlug: string,
  sellerInputUUID: string,
) => {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'UpdateAnswers',
      query: `mutation UpdateAnswers(
        $answers: String!
        $sellerFlowLastCompletedSlug: String!
        $sellerInputUUID: String!
      ) {
        reception {
          updateSellerInput(
            input: {
              uuid: $sellerInputUUID
              answers: $answers
              sellerFlowLastCompletedSlug: $sellerFlowLastCompletedSlug
            }
          ) {
            sellerInput {
              uuid
            }
          }
        }
      }`,
      variables: {
        answers: JSON.stringify(answers),
        sellerFlowLastCompletedSlug,
        sellerInputUUID,
      },
    },
    { headers: { 'apollographql-client-name': 'cosmos' } },
  ).then(handleResponse);
};

function handleResponse(res: Response) {
  if (res.ok) {
    return res.json();
  }
  Sentry.withScope?.((scope) => {
    scope.setExtras({ resonse: res });
    Sentry.captureMessage?.('Non-200 response received in cosmos api call');
  });
  return;
}

const getGrainID = (customerUUID?: string, anonId?: string) => {
  const anonymousId = getAnonymousId() || anonId;
  return {
    primaryId: { customerUuid: customerUUID ? customerUUID : null },
    temporaryId: { anonymousId: anonymousId ? anonymousId : null },
  };
};

export async function getFeatureFlag(
  name: string,
  customerUUID?: string,
  options: {
    isLoading?: boolean;
    autoImpression?: boolean;
    defaultValue?: string;
    props?: GetSeveralFeatureFlagsRequestQueryVariables['props'];
  } = {},
  anonymousId?: string,
) {
  const { isLoading = false, defaultValue = 'control', props } = options;
  const customer = getGrainID(customerUUID, anonymousId);
  const localOverride = getLocalFeatureFlagOverrides(name);
  if (localOverride) {
    return Promise.resolve(localOverride);
  }
  if (isLoading) {
    return defaultValue;
  }
  const res = await doBFFPost(
    ATHENA_URL,
    {
      operationName: 'GetSeveralFeatureFlagsRequest',
      query: `query GetSeveralFeatureFlagsRequest(
          $grain: OdProtosVariantData_GrainInput
          $keys: [String!]
          $props: GoogleProtobuf_StructInput
        ) {
          variant {
            getFeatureFlags(input: { grain: $grain, keys: $keys, props: $props }) {
              variants {
                key
                value {
                  value
                }
              }
            }
          }
        }`,
      variables: {
        grain: {
          grain: {
            customer,
          },
        },
        keys: [name],
        props,
      },
    },
    { headers: { 'apollographql-client-name': 'cosmos' } },
  )
    .then((res) => {
      if (res.ok) {
        return res.json();
      }
      return;
    })
    .catch((e) => Sentry.captureException?.(e));
  // Returns default if there is no value for the FF — including a key the
  // variant service does not know, which comes back as an empty variants list.
  const value = res?.data?.variant?.getFeatureFlags?.variants?.[0]?.value?.value;
  return value || defaultValue;
}

export async function GetUnlockPropertyInfo(addressId: string) {
  try {
    return getAthenaClient().request(GetUnlockPropertyInfoDocument, { addressId });
  } catch (e) {
    Sentry.captureException?.(e);
    return;
  }
}

export async function GetGeoShapeFromUserLocation(
  latitude: number,
  longitude: number,
  shapeGeoOption: OdProtosGeoDataStoreServices_ShapeGeoOption,
) {
  try {
    return getAthenaClient().request(GetGeoShapeFromUserLocationDocument, {
      latitude,
      longitude,
      shapeGeoOption,
    });
  } catch (e) {
    Sentry.captureException?.(e);
    return;
  }
}

export async function GetPropertiesFromGeoShape(
  shape: string,
  filters: OdProtosPropertyDataStoreServices_PropertyFiltersInput,
  paginationRequest: OdProtosPropertyDataStoreServices_PaginationRequestInput,
) {
  try {
    return getAthenaClient().request(GetPropertiesFromGeoShapeDocument, {
      shape,
      filters,
      paginationRequest,
    });
  } catch (e) {
    Sentry.captureException?.(e);
    return;
  }
}

export async function AddressAutocomplete(
  query: string,
  filters: PropertyFilters,
  pageInput: PageInput,
  latitude?: number,
  longitude?: number,
) {
  try {
    return getAthenaClient().request(AddressAutocompleteDocument, {
      query,
      filters,
      latitude,
      longitude,
      pageInput,
    });
  } catch (e) {
    Sentry.captureException?.(e);
    return;
  }
}

export function unsubscribeMailer(addressUuid: string) {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'UnsubscribeMailer',
      query: `mutation UnsubscribeMailer($address: OdProtosWebServicesMarketing_UnsubscribeMailerRequestInput!) {
      marketing {
        unsubscribeMailer(input: $address) {
          _empty
        }
      }
    }`,
      variables: {
        address: {
          addressToken: addressUuid,
        },
      },
    },
    { headers: { 'apollographql-client-name': 'cosmos' } },
  )
    .then(handleResponse)
    .catch((e) => Sentry.captureException?.(e));
}

export function requestNewToken(expiredToken: string) {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'RequestNewToken',
      query: `mutation RequestNewToken($expiredToken: String!) {
        passwordless {
          requestNewToken(input: {token: $expiredToken }) {
            token
          }
        }
      }`,
      variables: { expiredToken },
    },
    { headers: { 'apollographql-client-name': 'cosmos' } },
  )
    .then(handleResponse)
    .catch((e) => Sentry.captureException?.(e));
}

export function mintInstallResumeTokenFromToken(token: string) {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'MintInstallResumeTokenFromToken',
      query: `mutation MintInstallResumeTokenFromToken($token: String!) {
        mintInstallResumeTokenFromToken(token: $token) {
          odlToken
        }
      }`,
      variables: { token },
    },
    { headers: { 'apollographql-client-name': 'cosmos' } },
  )
    .then(handleResponse)
    .catch((e) => Sentry.captureException?.(e));
}

export function requestNewTokenByEmail(email: string, redirectUrl?: string) {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'RequestNewToken',
      query: `mutation RequestNewToken($email: String!, $redirectUrl: String) {
        passwordless {
          requestNewToken(input: {email: $email, redirectUrl: $redirectUrl}) {
            token
          }
        }
      }`,
      variables: { email, redirectUrl },
    },
    { headers: { 'apollographql-client-name': 'cosmos' } },
  )
    .then(handleResponse)
    .catch((e) => Sentry.captureException?.(e));
}

export async function updateCurrentCustomer(
  params: OdProtosCustomerV1Services_CustomerRequests_UpdateCurrentInput,
) {
  try {
    return await getAthenaClient().request(UpdateCurrentCustomerDocument, {
      customerUpdates: params,
    });
  } catch (e) {
    Sentry.captureException?.(e);
    return;
  }
}

export async function getCurrentCustomerUuid(): Promise<string | undefined> {
  try {
    const res = await getAthenaClient().request(GetCurrentCustomerDocument);
    return res.customerService.getCurrentCustomer?.customer?.uuid ?? undefined;
  } catch (e) {
    Sentry.captureException?.(e);
    return undefined;
  }
}

export async function upsertCommPreference(
  input: OdProtosCustomerV1Services_UpsertCommPreferenceRequestInput,
) {
  try {
    return await getAthenaClient().request(UpsertCommPreferenceDocument, { input });
  } catch (e) {
    Sentry.captureException?.(e);
    return;
  }
}

export const CancelBuyDirectOfferStatus = {
  SUCCESS_OFFER_CANCELLED: 'SUCCESS_OFFER_CANCELLED',
  SUCCESS_TASK_CREATED: 'SUCCESS_TASK_CREATED',
} as const;

export function cancelBuyDirectOffer(listingOfferUuid: string) {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'CancelBuyDirectOffer',
      query: `mutation CancelBuyDirectOffer($listingOfferUuid: String!) {
        webBuyDirectListingOffer {
          cancelBuyDirectOffer(input: { listingOfferUuid: $listingOfferUuid }) {
            __typename
            status
          }
        }
      }`,
      variables: { listingOfferUuid },
    },
    { headers: { 'apollographql-client-name': 'cosmos' } },
  )
    .then(handleResponse)
    .catch((e) => {
      Sentry.captureException?.(e);
      return null;
    });
}

// Buy Direct Checkout API functions
export async function getBuyDirectPropertyInfo(addressToken: string) {
  try {
    return await getAthenaClient().request(GetBuyDirectPropertyInfoDocument, { addressToken });
  } catch (e) {
    Sentry.captureException?.(e);
    throw e;
  }
}

export async function getResaleOffers(customerId: string) {
  try {
    return await getAthenaClient().request(GetResaleOffersDocument, { customerId });
  } catch (e) {
    Sentry.captureException?.(e);
    throw e;
  }
}

export function listUnits(addressUUID: string) {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'ListUnits',
      query: `query ListUnits($addressUUID: String!, $limit: Float!) {
        casa {
          listUnits(input: { uuid: $addressUUID, limit: $limit }) {
            addresses{
              uuid
              result
            }
          }
        }
      }`,
      variables: {
        addressUUID,
        limit: 10,
      },
    },
    { headers: { 'apollographql-client-name': 'cosmos' } },
  )
    .then(handleResponse)
    .catch((e) => Sentry.captureException?.(e));
}

export function standardizeAddress(address: IAddressInputAddress) {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'StandardizeAddress',
      query: `query StandardizeAddress($address: OdProtosCasaServices_CasaRequest_FindByFieldsInput!) {
      casa {
        findByFields(input: $address) {
          address {
            uuid
            street
            city
            state
            unitNumber
            unitType
            postalCode
            latitude
            longitude
            result
          }
        }
      }
    }`,
      variables: {
        address: {
          city: address.city,
          postalCode: address.postal_code,
          state: address.state,
          street: address.street1,
          unitNumber: address.unit,
        },
      },
    },
    { headers: { 'apollographql-client-name': 'cosmos' } },
  )
    .then(handleResponse)
    .catch((e) => Sentry.captureException?.(e));
}

export type CreateMortgageLeadInput = {
  customerUuid?: string;
  channel?: string;
  addressToken?: string;
  propertyType?: string;
  targetMoveDate?: string;
  targetPrice?: number;
  targetDownPayment?: number;
  targetLocation?: string;
  employmentStatus?: string;
  householdIncomeMin?: number;
  householdIncomeMax?: number;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  currentAddress?: string;
};

export function createMortgageLead(input: CreateMortgageLeadInput) {
  const mutation = `
    mutation MortgageLead($input: OdProtosCustomerFinancingServiceServices_CustomerFinancingRequests_CreateMortgageLeadInput!) {
      customerFinancingService {
        createMortgageLead(input: $input) {
          mortgageLead {
            mortgageLeadId
            customerUuid
          }
        }
      }
    }
  `;

  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'MortgageLead',
      query: mutation,
      variables: {
        input: {
          customerUuid: input.customerUuid,
          channel: 'CUSTOMER_EXPERIENCE',
          addressToken: input?.addressToken,
          propertyType: input.propertyType,
          targetMoveDate: input.targetMoveDate,
          targetPrice: input.targetPrice,
          targetDownPayment: input.targetDownPayment,
          targetLocation: input.targetLocation,
          employmentStatus: input.employmentStatus,
          householdIncomeMin: input.householdIncomeMin,
          householdIncomeMax: input.householdIncomeMax,
          firstName: input.firstName,
          lastName: input.lastName,
          phone: input.phone,
          email: input.email,
          currentAddress: input.currentAddress,
        },
      },
    },
    { headers: { 'apollographql-client-name': 'cosmos' } },
  )
    .then(async (response) => {
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`GraphQL mutation failed: ${errorText}`);
      }
      const result = await response.json();
      if (result.errors) {
        throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
      }
      return result;
    })
    .catch((e) => {
      Sentry.captureException?.(e);
      throw e;
    });
}
